import AsyncStorage from '@react-native-async-storage/async-storage';
import {
	HIDE_LOADING_MODAL,
	INITIALISE_MENU,
	SET_LAST_VALID_USER_SESSION,
	SHOW_AUTH_MODAL,
	SUCCESS,
} from '@reduxLocal/actions/types';
import { store } from '@reduxLocal/persistState';
import { buildDispatchData } from '../utils';
import { performBiometricAuth } from '@navigation/screens/TopLevel/LoginScreen/utils/authSessionHandler';
import { hydrateOfflineModeEntitlement, isOfflineModeEntitled } from './offlineEntitlement';
import { computeSessionExpireAt } from '@utils/sessionConstants';

export const getValidCachedSession = async () => {
	try {
		const rawUserData = await AsyncStorage.getItem('userData');
		if (!rawUserData) return null;

		const parsedUserData = JSON.parse(rawUserData);
		const isExpired =
			parsedUserData?.expireAt &&
			new Date(parsedUserData.expireAt) < new Date();

		if (isExpired) {
			await AsyncStorage.removeItem('userData');
			return null;
		}

		return parsedUserData;
	} catch (error) {
		console.error('Failed to read cached session for offline login:', error);
		return null;
	}
};

export async function completeOfflineLogin(
	dispatch: (action: unknown) => void,
	cachedSession: any,
	cb?: (data: any, success: boolean) => void
): Promise<boolean> {
	const authResult = await performBiometricAuth({
		promptMessage: 'Authenticate to continue in offline mode',
	});

	if (!authResult.success) {
		store.dispatch({ type: HIDE_LOADING_MODAL });
		store.dispatch({
			type: SHOW_AUTH_MODAL,
			data: {
				content:
					'Offline login cancelled. Biometric authentication is required to use your cached session.',
				type: 'error',
			},
		});
		return false;
	}

	const amendedData = {
		...cachedSession,
		success: true,
		offlineSession: true,
		pendingSync: true,
		expireAt:
			cachedSession?.expireAt ?? computeSessionExpireAt(true),
	};

	store.dispatch({ type: HIDE_LOADING_MODAL });
	store.dispatch({
		type: SHOW_AUTH_MODAL,
		data: {
			content:
				'Server unreachable — signed in with your cached session. Changes to data, projects, and calendar will sync when the server is back.',
			type: 'error',
		},
	});
	dispatch(buildDispatchData(SUCCESS, amendedData));
	dispatch({
		type: SET_LAST_VALID_USER_SESSION,
		payload: amendedData,
	});
	store.dispatch({ type: INITIALISE_MENU });
	cb?.(amendedData, true);
	return true;
}

/**
 * Shared offline login when device is offline OR API is unreachable.
 */
export async function attemptOfflineLoginFallback(
	dispatch: (action: unknown) => void,
	cb?: (data: any, success: boolean) => void
): Promise<boolean> {
	await hydrateOfflineModeEntitlement();
	if (!isOfflineModeEntitled()) {
		store.dispatch({ type: HIDE_LOADING_MODAL });
		store.dispatch({
			type: SHOW_AUTH_MODAL,
			data: {
				content:
					'Offline login requires an active subscription. Please reconnect to sign in, or upgrade your plan.',
				type: 'error',
			},
		});
		return false;
	}

	const cachedSessionFromRedux =
		store.getState()?.promiseData?.lastValidUserSession;
	let cachedSession = cachedSessionFromRedux;

	const isReduxSessionExpired =
		cachedSession?.expireAt &&
		new Date(cachedSession.expireAt) <= new Date();

	if (!cachedSession || isReduxSessionExpired) {
		cachedSession = await getValidCachedSession();
	}

	if (!cachedSession) {
		store.dispatch({ type: HIDE_LOADING_MODAL });
		store.dispatch({
			type: SHOW_AUTH_MODAL,
			data: {
				content:
					'No valid cached session is available. Sign in online once to enable offline access.',
				type: 'error',
			},
		});
		return false;
	}

	return completeOfflineLogin(dispatch, cachedSession, cb);
}
